import { useState } from 'react';
import { Button, ButtonGroup, Form, FormLayout, TextField, Tooltip } from '@customerio/pluma-components/react';

type ConnectFormProps = {
  /** The stored write key, or an empty string when not connected. */
  writeKey: string;
  isConnected: boolean;
  /**
   * Whether the reader may create the connection. Connecting and removing are each
   * their own grant, held separately from editing an existing connection.
   */
  canConnect: boolean;
  canRemove: boolean;
  isConnecting: boolean;
  isDisconnecting: boolean;
  isSaving: boolean;
  /**
   * Awaited, so Pluma's `Form` locks the whole fieldset for the duration — the
   * re-entry guard that stops a double submit creating two records.
   */
  onConnect: (writeKey: string) => Promise<void>;
  onDisconnect: () => void;
};

/**
 * The write-key half of the page: connect with a Segment write key, or remove an
 * existing connection.
 *
 * The key goes read-only once connected, because the API takes it only on create
 * — changing it means removing the connection and connecting again.
 *
 * Connecting is gated on `create` rather than `edit`. Ember's modern form used
 * `edit` here, but its own legacy form on this page used `create`, and the two
 * are separately grantable — so a role given `edit` alone could set up a
 * connection it was never granted.
 *
 * Remove is held while a settings save is in flight: the save writes the cache
 * slot this page reads from, so a delete landing mid-save would be overwritten by
 * the late response and the connector would look connected again.
 */
export function ConnectForm({
  writeKey,
  isConnected,
  canConnect,
  canRemove,
  isConnecting,
  isDisconnecting,
  isSaving,
  onConnect,
  onDisconnect,
}: ConnectFormProps) {
  const [key, setKey] = useState(writeKey);

  const isValid = key.trim() !== '';

  const handleSubmit = async () => {
    if (!isValid) return;
    await onConnect(key);
  };

  return (
    <Form onSubmit={handleSubmit}>
      <FormLayout>
        {/* Masked for session replay: this is a third-party credential. */}
        <TextField
          className="fs-mask"
          label="Segment write key"
          value={key}
          onInput={(event) => setKey(event.target.value)}
          isDisabled={isConnected || !canConnect}
        />

        <ButtonGroup groupVariant="spaced">
          {isConnected ? (
            <Button
              variant="secondary"
              isCritical
              isDisabled={!canRemove || isSaving}
              isLoading={isDisconnecting}
              onClick={onDisconnect}
            >
              Remove integration
            </Button>
          ) : (
            <Tooltip
              isCustom={true}
              isDisabled={canConnect}
              content="You don't have permission to set up this integration. If you'd like access, let an admin know."
            >
              {(context) => (
                <Button
                  ref={context.reference.setReference}
                  {...context.reference.getReferenceProps()}
                  type="submit"
                  variant="primary"
                  isDisabled={!canConnect || !isValid}
                  isLoading={isConnecting}
                >
                  Connect Segment
                </Button>
              )}
            </Tooltip>
          )}
        </ButtonGroup>
      </FormLayout>
    </Form>
  );
}
